import { useTranslation } from 'react-i18next';
import type { CSSProperties, ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ProfilePhoto } from '@/types';

export interface SortablePhotoTileProps {
  photo: ProfilePhoto;
  /** 1-based position, used for the accessible label. */
  index: number;
  gridRow?: string;
  /** Disables dragging while a sibling upload is in flight. */
  disabled?: boolean;
  children: ReactNode;
}

/**
 * Draggable photo slot; the tile itself follows the pointer. The absolute
 * overlay holds the dnd-kit listeners because the tile Button is disabled
 * when filled. The remove button floats above the overlay (z-index).
 */
export function SortablePhotoTile({
  photo,
  index,
  gridRow,
  disabled = false,
  children,
}: SortablePhotoTileProps) {
  const { t } = useTranslation('profile');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ id: photo.id, disabled });

  const style: CSSProperties = {
    gridRow,
    transform: CSS.Transform.toString(transform),
    transition,
    // Lift the dragged tile above sibling overlays.
    zIndex: isDragging ? 3 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative flex h-full flex-col justify-center">
      <div
        style={{
          borderRadius: 'var(--radius-4)',
          boxShadow: isDragging ? '0 8px 24px rgb(0 0 0 / 0.35)' : undefined,
        }}
      >
        {children}
      </div>
      {!disabled && (
        <div
          {...attributes}
          {...listeners}
          aria-label={t('edit.photos.tileLabel', { index })}
          className="absolute inset-0"
          style={{
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'manipulation',
            zIndex: 1,
            borderRadius: 'var(--radius-4)',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
            boxShadow: isOver && !isDragging ? 'inset 0 0 0 2px var(--accent-9)' : undefined,
          }}
        />
      )}
    </div>
  );
}
